import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Film,
  Loader2,
  Rocket,
  Upload,
} from 'lucide-react'
import clsx from 'clsx'

import {
  createEpisodeExport,
  downloadExportManifest,
  listScriptDocuments,
  listScriptEpisodes,
  listTimeline,
  publishToWorks,
  reorderTimeline,
  type EpisodeExport,
  type ScriptEpisodeSummary,
  type Timeline,
  type TimelineClip,
} from '@/api/studio-production'
import { CatalogListPageShell } from '@/shared/components/catalog/CatalogListPageShell'
import { PageHeading } from '@/shared/components/PageHeading'
import { ProtoBtn, ProtoIconBtn, ProtoSelect } from '@/features/studio/components/ui-kit'
import { formatEpisodeLabel } from '@/features/studio/script/season'
import { Label } from '@/shared/components/ui/label'
import { Button } from '@/shared/components/ui/button'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'

const PX_PER_SEC = 32
const TRACK_HEIGHT = 132
const MIN_CLIP_WIDTH = 56

function clipWidth(durationSec: number): number {
  return Math.max(MIN_CLIP_WIDTH, Math.round(durationSec * PX_PER_SEC))
}

function isApprovedClip(clip: TimelineClip): boolean {
  return clip.status === 'approved' && Boolean(clip.media_url)
}

function moveClip(items: TimelineClip[], from: number, to: number): TimelineClip[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return items
  const next = [...items]
  const [removed] = next.splice(from, 1)
  next.splice(to, 0, removed)
  return next
}

export function TimelinePage() {
  const { productionId = '' } = useParams()
  const { t } = useTranslation()
  const [episodeNo, setEpisodeNo] = useState(1)
  const [timeline, setTimeline] = useState<Timeline | null>(null)
  const [clips, setClips] = useState<TimelineClip[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastExport, setLastExport] = useState<EpisodeExport | null>(null)
  const [publishResult, setPublishResult] = useState<string | null>(null)
  const [episodes, setEpisodes] = useState<ScriptEpisodeSummary[]>([])
  const [episodeTarget, setEpisodeTarget] = useState(1)
  const [episodesPerSeason, setEpisodesPerSeason] = useState(0)
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null)
  const [dropIndex, setDropIndex] = useState<number | null>(null)
  const [reordering, setReordering] = useState(false)
  const didDragRef = useRef(false)
  const trackViewportRef = useRef<HTMLDivElement>(null)
  const [viewportWidth, setViewportWidth] = useState(320)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const scriptRes = await listScriptDocuments(productionId, { limit: 50, offset: 0 })
        const sid = scriptRes.items[0]?.id
        if (!sid || cancelled) {
          if (!cancelled) setEpisodes([])
          return
        }
        const epRes = await listScriptEpisodes(productionId, sid)
        if (cancelled) return
        setEpisodes(epRes.items)
        setEpisodeTarget(epRes.episode_target)
        setEpisodesPerSeason(epRes.episodes_per_season)
      } catch {
        if (!cancelled) setEpisodes([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [productionId])

  const episodeOptions = useMemo(() => {
    const source =
      episodes.length > 0
        ? episodes.map((ep) => ep.episode_no)
        : Array.from({ length: Math.max(episodeTarget, 1) }, (_, i) => i + 1)
    const unique = [...new Set(source)].sort((a, b) => a - b)
    return unique.map((no) => {
      const ep = episodes.find((item) => item.episode_no === no)
      const label = formatEpisodeLabel(no, episodesPerSeason, episodeTarget)
      const title = ep?.title?.trim()
      return { value: no, label: title ? `${label} · ${title}` : label }
    })
  }, [episodes, episodeTarget, episodesPerSeason])

  useEffect(() => {
    if (episodeOptions.length === 0) return
    if (!episodeOptions.some((opt) => opt.value === episodeNo)) {
      setEpisodeNo(episodeOptions[0].value)
    }
  }, [episodeOptions, episodeNo])

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await listTimeline(productionId, episodeNo)
      const items = data.items ?? []
      setTimeline({ ...data, items })
      setClips(items)
      setSelectedId((prev) => {
        if (prev && items.some((c) => c.public_id === prev)) return prev
        return items[0]?.public_id ?? null
      })
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'timeline.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [productionId, episodeNo])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    const el = trackViewportRef.current
    if (!el) return
    const syncWidth = () => {
      const w = el.clientWidth
      if (w > 0) setViewportWidth(w)
    }
    syncWidth()
    const ro = new ResizeObserver(() => syncWidth())
    ro.observe(el)
    return () => ro.disconnect()
  }, [loading, clips.length])

  const selectedIndex = useMemo(
    () => clips.findIndex((c) => c.public_id === selectedId),
    [clips, selectedId],
  )
  const selectedClip = selectedIndex >= 0 ? clips[selectedIndex] : null

  const clipLayouts = useMemo(() => {
    const naturalWidths = clips.map((clip) => clipWidth(clip.duration_sec || 0))
    const totalNatural = naturalWidths.reduce((sum, w) => sum + w, 0)
    const available = Math.max(viewportWidth, 320)
    const fillContainer = totalNatural <= available
    const widths = fillContainer
      ? naturalWidths.map((w) =>
          totalNatural > 0 ? (w / totalNatural) * available : available / Math.max(clips.length, 1),
        )
      : naturalWidths

    let left = 0
    let elapsedSec = 0
    return clips.map((clip, index) => {
      const width = widths[index]
      const layout = { clip, left, width, startSec: elapsedSec }
      left += width
      elapsedSec += clip.duration_sec || 0
      return layout
    })
  }, [clips, viewportWidth])

  const trackWidthPx = useMemo(() => {
    const contentWidth = clipLayouts.reduce((sum, l) => sum + l.width, 0)
    return Math.max(viewportWidth, contentWidth)
  }, [clipLayouts, viewportWidth])
  const trackDurationSec = useMemo(
    () => clips.reduce((sum, c) => sum + (c.duration_sec || 0), 0),
    [clips],
  )
  const rulerMarks = useMemo(() => {
    const marks = [{ left: 0, sec: 0 }]
    let sec = 0
    for (const layout of clipLayouts) {
      sec += layout.clip.duration_sec || 0
      marks.push({ left: layout.left + layout.width, sec })
    }
    return marks
  }, [clipLayouts])

  const selectAdjacentClip = useCallback(
    (dir: -1 | 1) => {
      const next = selectedIndex + dir
      if (next < 0 || next >= clips.length) return
      setSelectedId(clips[next].public_id)
    },
    [clips, selectedIndex],
  )

  const persistClipOrder = useCallback(
    async (nextClips: TimelineClip[], prevClips: TimelineClip[]) => {
      setReordering(true)
      setError(null)
      try {
        await reorderTimeline(
          productionId,
          episodeNo,
          nextClips.map((clip) => clip.public_id),
        )
        setTimeline((prev) => (prev ? { ...prev, items: nextClips } : prev))
      } catch (e) {
        setClips(prevClips)
        setError(resolveApiErrorMessage(e, 'timeline.reorderFailed'))
      } finally {
        setReordering(false)
      }
    },
    [productionId, episodeNo],
  )

  const handleClipDrop = useCallback(
    (from: number, to: number) => {
      if (from === to || reordering || busy) return
      const prevClips = clips
      const nextClips = moveClip(prevClips, from, to)
      setClips(nextClips)
      void persistClipOrder(nextClips, prevClips)
    },
    [busy, clips, persistClipOrder, reordering],
  )

  const handleClipDragStart = useCallback((index: number) => {
    didDragRef.current = true
    setDraggingIndex(index)
    setDropIndex(index)
  }, [])

  const handleClipDragEnd = useCallback(() => {
    setDraggingIndex(null)
    setDropIndex(null)
    window.setTimeout(() => {
      didDragRef.current = false
    }, 0)
  }, [])

  useEffect(() => {
    if (!selectedId || !trackViewportRef.current) return
    const layout = clipLayouts.find((item) => item.clip.public_id === selectedId)
    if (!layout) return
    const viewport = trackViewportRef.current
    const padding = 12
    const viewLeft = viewport.scrollLeft
    const viewRight = viewLeft + viewport.clientWidth
    const clipLeft = layout.left
    const clipRight = layout.left + layout.width
    if (clipLeft < viewLeft + padding) {
      viewport.scrollLeft = Math.max(0, clipLeft - padding)
    } else if (clipRight > viewRight - padding) {
      viewport.scrollLeft = clipRight - viewport.clientWidth + padding
    }
  }, [selectedId, clipLayouts])

  async function handleExport() {
    setBusy(true)
    setError(null)
    try {
      const exp = await createEpisodeExport(productionId, episodeNo)
      setLastExport(exp)
      downloadExportManifest(exp)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'timeline.exportFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function handlePublish(publish: boolean) {
    setBusy(true)
    setError(null)
    setPublishResult(null)
    try {
      const res = await publishToWorks(productionId, { publish })
      setPublishResult(
        t('timeline.publishSuccess', {
          id: res.work_public_id,
          n: res.episodes_synced,
          status: res.work_status,
        }),
      )
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'timeline.publishFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <CatalogListPageShell className="pb-8">
      <PageHeading icon={Film} tone="indigo" title={t('timeline.title')} subtitle={t('timeline.subtitle')}>
        <div className="flex w-full flex-wrap items-center justify-end gap-2 lg:w-auto">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">{t('timeline.episode')}</Label>
            <ProtoSelect
              value={String(episodeNo)}
              disabled={busy || reordering || episodeOptions.length === 0}
              onChange={(e) => setEpisodeNo(Number(e.target.value))}
              className="min-w-[7rem] !text-xs"
              aria-label={t('timeline.episode')}
            >
              {episodeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </ProtoSelect>
          </div>
          <ProtoBtn size="sm" variant="soft" tone="amber" disabled={busy} onClick={() => void handleExport()}>
            <Download className="h-3.5 w-3.5" />
            {t('timeline.exportManifest')}
          </ProtoBtn>
          <ProtoBtn size="sm" variant="secondary" disabled={busy} onClick={() => void handlePublish(false)}>
            <Upload className="h-3.5 w-3.5" />
            {t('timeline.syncWorks')}
          </ProtoBtn>
          <ProtoBtn size="sm" disabled={busy} onClick={() => void handlePublish(true)}>
            <Rocket className="h-3.5 w-3.5" />
            {t('timeline.publishWorks')}
          </ProtoBtn>
        </div>
      </PageHeading>

      {error ? <p className="mb-3 text-sm text-rose-300">{t(error, { defaultValue: error })}</p> : null}
      {publishResult ? <p className="mb-3 text-sm text-emerald-300">{publishResult}</p> : null}

      {timeline ? (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="rounded-md border border-white/[0.06] bg-[#12121a] px-2 py-1 font-mono text-[11px] text-zinc-300">
            {t('timeline.stats.duration', { sec: (timeline.total_duration_sec ?? 0).toFixed(1) })}
          </span>
          <span className="rounded-md border border-white/[0.06] bg-[#12121a] px-2 py-1 text-[11px] text-zinc-400">
            {t('timeline.stats.approved', { n: timeline.approved_clip_count })}
          </span>
          <span className="rounded-md border border-white/[0.06] bg-[#12121a] px-2 py-1 text-[11px] text-zinc-400">
            {t('timeline.stats.ratio', { ratio: timeline.aspect_ratio })}
          </span>
          <span className="rounded-md border border-white/[0.06] bg-[#12121a] px-2 py-1 text-[11px] text-zinc-500">
            {t('timeline.stats.clips', { n: clips.length })}
          </span>
          {lastExport ? (
            <span className="text-[11px] text-zinc-600">
              {t('timeline.lastExport', { id: lastExport.public_id.slice(0, 8) })}
            </span>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <div className="flex h-36 items-center justify-center rounded-xl border border-white/[0.06] bg-[#0a0a12]">
          <Loader2 className="h-7 w-7 animate-spin text-indigo-400" />
        </div>
      ) : clips.length === 0 ? (
        <p className="text-sm text-zinc-500">{t('timeline.empty')}</p>
      ) : (
        <div className="space-y-3">
          <section className="overflow-hidden rounded-xl border border-white/[0.08] bg-[#08080f]">
            <div className="flex items-center justify-between border-b border-white/[0.06] px-3 py-1.5">
              <span className="text-[10px] font-medium tracking-wide text-zinc-500 uppercase">
                {t('timeline.trackLabel')}
              </span>
              <span className="font-mono text-[10px] text-zinc-600">
                {t('timeline.sequenceLength', { sec: trackDurationSec.toFixed(1) })}
              </span>
            </div>

            <div ref={trackViewportRef} className="studio-scrollbar-hidden overflow-x-auto">
              <div className="relative" style={{ width: trackWidthPx, minWidth: '100%' }}>
                <div
                  className="relative h-5 border-b border-white/[0.04] bg-[#0c0c14]"
                  style={{ width: '100%' }}
                >
                  {rulerMarks.map((mark, i) => (
                    <div
                      key={`${mark.left}-${mark.sec}`}
                      className="absolute top-0 flex h-full flex-col justify-end"
                      style={{ left: mark.left }}
                    >
                      <span className="mb-0.5 -translate-x-1/2 font-mono text-[9px] text-zinc-600">
                        {mark.sec.toFixed(mark.sec % 1 ? 1 : 0)}s
                      </span>
                      <div
                        className={clsx(
                          'w-px',
                          i === 0 ? 'h-2 bg-rose-400/70' : 'h-2 bg-zinc-700/80',
                        )}
                      />
                    </div>
                  ))}
                </div>

                <div
                  className="relative"
                  style={{ height: TRACK_HEIGHT, width: '100%' }}
                  onDragOver={(e) => {
                    if (draggingIndex === null) return
                    e.preventDefault()
                    e.dataTransfer.dropEffect = 'move'
                  }}
                >
                  {clipLayouts.map(({ clip, left, width }, index) => {
                    const approved = isApprovedClip(clip)
                    const selected = clip.public_id === selectedId
                    const dragging = draggingIndex === index
                    const dropTarget = dropIndex === index && draggingIndex !== null && draggingIndex !== index
                    return (
                      <Button
                        key={clip.public_id}
                        type="button"
                        variant="ghost"
                        draggable={!busy && !reordering}
                        disabled={busy || reordering}
                        onDragStart={(e) => {
                          handleClipDragStart(index)
                          e.dataTransfer.effectAllowed = 'move'
                          e.dataTransfer.setData('text/plain', clip.public_id)
                        }}
                        onDragEnd={handleClipDragEnd}
                        onDragOver={(e) => {
                          if (draggingIndex === null) return
                          e.preventDefault()
                          e.stopPropagation()
                          e.dataTransfer.dropEffect = 'move'
                          setDropIndex(index)
                        }}
                        onDrop={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          if (draggingIndex === null) return
                          handleClipDrop(draggingIndex, index)
                          handleClipDragEnd()
                        }}
                        onClick={() => {
                          if (didDragRef.current) return
                          setSelectedId(clip.public_id)
                        }}
                        className={clsx(
                          'group absolute top-0 h-auto cursor-grab overflow-hidden rounded-none border-r border-black/50 p-0 text-left active:cursor-grabbing',
                          selected
                            ? 'z-10 ring-2 ring-indigo-400/80 ring-inset'
                            : 'hover:brightness-110',
                          dragging && 'z-20 opacity-45',
                          dropTarget && 'ring-2 ring-amber-400/70 ring-inset',
                          approved ? 'bg-[#14141f]' : 'bg-[#101018]',
                        )}
                        style={{ left, width, height: TRACK_HEIGHT }}
                        aria-label={clip.slug}
                        title={t('timeline.dragToReorder')}
                      >
                        {clip.media_url ? (
                          <img
                            src={clip.media_url}
                            alt=""
                            draggable={false}
                            className="h-full w-full object-cover opacity-90"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center bg-[#0b0b13] text-[9px] text-zinc-600">
                            {t('storyboard.noPreview')}
                          </div>
                        )}

                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />

                        <div className="pointer-events-none absolute inset-x-0 bottom-0 px-1 pb-1">
                          <div className="flex items-end justify-between gap-1">
                            <span className="truncate font-mono text-[9px] text-white/90">{clip.slug}</span>
                            <span className="shrink-0 font-mono text-[9px] text-zinc-300">
                              {clip.duration_sec}s
                            </span>
                          </div>
                        </div>

                        <span
                          className={clsx(
                            'pointer-events-none absolute top-1 left-1 rounded px-1 py-px text-[8px] font-medium',
                            approved
                              ? 'bg-emerald-500/25 text-emerald-200'
                              : 'bg-zinc-800/80 text-zinc-400',
                          )}
                        >
                          {index + 1}
                        </span>
                      </Button>
                    )
                  })}
                </div>
              </div>
            </div>
          </section>

          {selectedClip ? (
            <section className="flex flex-wrap items-center gap-2 rounded-xl border border-white/[0.06] bg-[#12121a] px-3 py-2">
              <div className="h-20 w-[7rem] shrink-0 overflow-hidden rounded-md bg-[#0b0b13]">
                {selectedClip.media_url ? (
                  <img
                    src={selectedClip.media_url}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-[9px] text-zinc-600">
                    {t('storyboard.noPreview')}
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs text-indigo-200">{selectedClip.slug}</span>
                  <span className="text-[10px] text-zinc-500">
                    {t(`shots.status.${selectedClip.status}`, { defaultValue: selectedClip.status })}
                  </span>
                  <span className="font-mono text-[10px] text-zinc-600">{selectedClip.duration_sec}s</span>
                  <span className="text-[10px] text-zinc-600">
                    {t('timeline.clipIndex', { n: selectedIndex + 1, total: clips.length })}
                  </span>
                </div>
                <p className="mt-0.5 line-clamp-1 text-[11px] text-zinc-400">
                  {selectedClip.description || '—'}
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-1">
                <ProtoIconBtn
                  disabled={selectedIndex <= 0}
                  onClick={() => selectAdjacentClip(-1)}
                  aria-label={t('timeline.prevClip')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </ProtoIconBtn>
                <ProtoIconBtn
                  disabled={selectedIndex >= clips.length - 1}
                  onClick={() => selectAdjacentClip(1)}
                  aria-label={t('timeline.nextClip')}
                >
                  <ChevronRight className="h-4 w-4" />
                </ProtoIconBtn>
              </div>
            </section>
          ) : (
            <p className="text-xs text-zinc-600">{t('timeline.selectClip')}</p>
          )}
        </div>
      )}

    </CatalogListPageShell>
  )
}
